import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router";
import axios from "axios";
import { Apis } from "../util/Apis";
import fallbackVideo from "../assets/gifs/hot-seat-grande-infinito.webm";
import ContainerBackgroundWrapper from "../components/Backgrounds";
import { tourDuration } from "../components/hotseat/hotseatMotion";
import WinnerScreenReveal from "../components/hotseat/WinnerScreenReveal";
import { SiteContext } from "../context/SiteContext";
import HotSeatScene from "../components/hotseat/HotSeatScene";
import HotSeatLoading from "../components/hotseat/HotSeatLoading";
import Button from "../components/atoms/Buttons";

const Stage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: #100e15;
  opacity: var(--scene-opacity, 1);
  color: #fff0d7;
  overflow: hidden;
  canvas {
    display: block;
  }
`;
const Heading = styled.header`
  position: absolute;
  top: 0;
  inset-inline: 0;
  padding: clamp(16px, 3vh, 32px) 20px 38px;
  text-align: center;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(#100e15e8, #100e1599 65%, transparent);
  &[data-searching="true"] {
    width: 1px; height: 1px; padding: 0; overflow: hidden;
    clip-path: inset(50%); white-space: nowrap;
  }
  span {
    color: #ebc688;
    letter-spacing: 4px;
    font-size: 12px;
  }
  h1 {
    margin: 8px 0 0;
    font-size: clamp(20px, 2.8vw, 38px);
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;
const Message = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 80px 24px 24px;
  text-align: center;
  background: radial-gradient(ellipse, #331820, #100e15);
  video {
    width: min(60%, 640px);
    max-height: 55vh;
    object-fit: contain;
  }
  p {
    max-width: 580px;
    line-height: 1.6;
  }
  @media (prefers-reduced-motion: reduce) {
    video {
      display: none;
    }
  }
`;

// A mount owns one generation request. Re-subscribing in React StrictMode or
// recovering a failed result read must never generate another winner.
function Draw({ id, salaId }) {
  const navigate = useNavigate();
  const { sorteosConf } = useContext(SiteContext);
  const revealLayer = useRef(null);
  const generation = useRef(null);
  const stage = useRef(null);
  const [startedAt] = useState(() => Date.now());
  const navigated = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState(null);
  const [fallback, setFallback] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [status, setStatus] = useState("Preparando la sala");
  const durationMs = tourDuration(salaId);
  const url = `${Apis.SORTEOS}/sorteos/${id}`;

  useEffect(() => {
    let active = true;
    if (!generation.current) {
      const job = { confirmed: false, promise: null };
      generation.current = job;
      job.promise = axios
        .post(`${url}/generate-winner-hot-seat`, null, { timeout: 60000 })
        .then(() => {
          job.confirmed = true;
          return axios.get(`${url}/winner`, { timeout: 20000 });
        });
    }
    generation.current.promise
      .then(({ data }) => {
        if (!active) return;
        setError(null);
        setOutcome({ winner: data && Number(data.confId) > 0 ? data : null });
      })
      .catch(() => {
        if (active) setError(generation.current.confirmed ? "result" : "generation");
      });
    return () => {
      active = false;
    };
  }, [url, attempt]);

  useEffect(() => {
    const abort = new AbortController();
    axios
      .get(`${url}/participants`, { signal: abort.signal, timeout: 15000 })
      .then(({ data }) => {
        if (!abort.signal.aborted && Array.isArray(data)) setParticipants(data);
      })
      .catch(() => {}); // Machine labels are optional; the winner is always supplied separately.
    return () => abort.abort();
  }, [url]);

  const complete = (transition) => {
    if (!outcome || error || navigated.current) return;
    navigated.current = true;
    navigate(`/winner-view-hotseat/${id}`, { state: {
      hotSeatReveal: {
        id,
        winner: outcome.winner,
        ...(transition?.screenRevealComplete ? { screenRevealComplete: true } : {}),
        ...(transition?.emptyRevealComplete ? { emptyRevealComplete: true } : {}),
      },
    } });
  };

  useEffect(() => {
    if (!fallback || !outcome || error) return;
    const timer = setTimeout(
      () => {
        if (!navigated.current) {
          navigated.current = true;
          navigate(`/winner-view-hotseat/${id}`, { state: {
            hotSeatReveal: { id, winner: outcome.winner },
          } });
        }
      },
      Math.max(0, durationMs - (Date.now() - startedAt))
    );
    return () => clearTimeout(timer);
  }, [fallback, outcome, error, durationMs, navigate, id, startedAt]);

  const readResult = () => {
    setError(null);
    setSceneReady(false);
    setStatus("Consultando el resultado");
    generation.current.promise = axios.get(`${url}/winner`, { timeout: 20000 });
    setAttempt((value) => value + 1);
  };

  return (
    <>
      <Stage ref={stage}>
        {(sceneReady || error || fallback) && (status || error) && (
          <Heading data-searching={!error && !fallback && status === "Buscando la máquina ganadora"}>
            <span>HOT SEAT</span>
            <h1 role="status" aria-live="polite">
              {error ? "No se pudo consultar el resultado" : status}
            </h1>
          </Heading>
        )}
        {!error && !fallback && (
          <HotSeatScene
            outcome={outcome}
            participants={participants}
            durationMs={durationMs}
            greenCarpet={Number(salaId) === 332}
            onComplete={complete}
            onStatus={setStatus}
            onReady={() => setSceneReady(true)}
            onRevealFrame={(frame) => revealLayer.current?.update(frame)}
            onTransition={(progress) => {
              stage.current?.style.setProperty("--scene-opacity", String(1 - progress));
            }}
            onFallback={() => {
              revealLayer.current?.update(null);
              stage.current?.style.setProperty("--scene-opacity", "1");
              setFallback(true);
              setStatus("Buscando la máquina ganadora");
            }}
          />
        )}
        {!error && !fallback && <HotSeatLoading ready={sceneReady} green={Number(salaId) === 332} />}
        {!error && fallback && (
          <Message>
            <video
              src={fallbackVideo}
              autoPlay
              muted
              loop
              playsInline
              aria-label="Animación de máquinas tragamonedas"
            />
            <p>Esperando el resultado del sorteo…</p>
          </Message>
        )}
        {error && (
          <Message role="alert">
            <p>
              {error === "result"
                ? "El sorteo se realizó, pero no pudimos leer el resultado. Puedes consultarlo nuevamente."
                : "No pudimos confirmar la respuesta del sorteo. Revisa su estado antes de iniciar otro."}
            </p>
            {error === "result" && <Button onClick={readResult}>Consultar resultado</Button>}
            <Button variant="outline" onClick={() => navigate("/sorteo-list")}>Volver a sorteos</Button>
          </Message>
        )}
      </Stage>
      {!error && !fallback && (
        <WinnerScreenReveal
          ref={revealLayer}
          winner={outcome?.winner}
          showWinner={sorteosConf?.showWinnerHotSeat}
          green={Number(salaId) === 332}
        />
      )}
    </>
  );
}

export default function PlayHotSeat() {
  const { id } = useParams();
  const { salaId } = useContext(SiteContext);
  return (
    <ContainerBackgroundWrapper showBackgroundCard={false}>
      {salaId == null ? (
        <Stage>
          <HotSeatLoading />
        </Stage>
      ) : (
        <Draw key={`${salaId}:${id}`} id={id} salaId={salaId} />
      )}
    </ContainerBackgroundWrapper>
  );
}
